import { DatePipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';

import { AlertCardComponent } from '../../../../../../shared/components/alert-card/alert-card.component';
import { EmptyStateComponent } from '../../../../../../shared/components/empty-state/empty-state.component';
import { LoadingBoxDefaultListComponent } from '../../../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { AbntPipe } from '../../../../../../shared/pipes/abnt.pipe';
import { TabsScrollDirective } from '../../../../../../shared/directives/tabs-scroll.directive';
import { CompraService } from '../../../../../../shared/services/apis/compras/compra.service';
import { LookupService } from '../../../../../../shared/services/apis/produto/produto-lookup.service';
import { VendaDocumentoService } from '../../../../../../shared/services/apis/vendas/venda-documento.service';
import { ControleEstoqueLocalStruct } from '../../../../../../shared/services/structs/produto/controle-estoque-local.struct';
import { ProdutoCompraPrevistaStruct } from '../../../../../../shared/services/structs/produto/produto-compra-prevista.struct';
import { VendaDoProdutoParaClienteStruct } from '../../../../../../shared/services/structs/vendas/venda-do-produto-cliente.struct';
import { KanbanCor } from '../../../../../../shared/services/models/vendas/kanban-cor.model';
import { PvCorVm, resolverCor } from '@shared/helpers/kanban-cor.helper';

/** O item cujo estoque se quer ver. */
export interface VdEstoqueDialogData {
    idProduto: number;
    descricao: string;
    codigoSku: string | null;
    codigoNcm: string | null;
    siglaUnidade: string | null;

    /** A quantidade lançada na linha — é dela que sai o "estoque depois da venda". */
    quantidade: number;

    /** O cliente do DOCUMENTO. Nulo enquanto ninguém o escolheu: a aba de vendas fica vazia. */
    idPessoaCliente: number | null;

    /** O catálogo de cores do kanban, que a tela já carregou nas configurações. */
    coresKanban: KanbanCor[];
}

/**
 * Detalhes do estoque do item (§6.3).
 *
 * Diálogo de LEITURA, de altura fixa: trocar de aba não redimensiona nada, o conteúdo rola por
 * dentro do `modal-content`. Abre sob demanda pelo botão da linha porque cada aba é uma consulta por produto —
 * carregá-las junto com o documento pagaria por item o que quase nunca se olha.
 *
 * **Três abas, e não as quatro da §6.3.** A "estoque multiempresa" ficou de fora por decisão do
 * Pedro (22/09): o estoque multiempresa ainda não funciona de verdade no HUB, e desenhar a aba
 * agora seria mostrar número em que ninguém pode confiar.
 *
 * As três fontes são INDEPENDENTES e degradam sozinhas: a API de Compras fora do ar deixa a aba
 * "compras previstas" vazia e não impede ver o saldo, que é o motivo mais comum de abrir isto.
 */
@Component({
    selector: 'app-vd-estoque-dialog',
    standalone: true,
    imports: [
        AbntPipe,
        AlertCardComponent,
        DatePipe,
        EmptyStateComponent,
        LoadingBoxDefaultListComponent,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatTableModule,
        MatTabsModule,
        TabsScrollDirective,
    ],
    templateUrl: './vd-estoque-dialog.component.html',
    styleUrl: './vd-estoque-dialog.component.css',
})
export class VdEstoqueDialogComponent implements OnInit {
    public carregandoEstoque: boolean = true;
    public carregandoVendas: boolean = true;
    public carregandoCompras: boolean = true;

    public locais: ControleEstoqueLocalStruct[] = [];
    public vendas: VendaDoProdutoParaClienteStruct[] = [];
    public compras: ProdutoCompraPrevistaStruct[] = [];

    public readonly colunasEstoque: string[] = ['localizacao', 'fisico', 'reservado', 'disponivel'];
    public readonly colunasVendas: string[] = ['documento', 'data', 'etapa', 'quantidade', 'valorUnitario', 'valorTotal'];
    public readonly colunasCompras: string[] = ['solicitacao', 'estagio', 'quantidade', 'previsao'];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: VdEstoqueDialogData,
        private dialogRef: MatDialogRef<VdEstoqueDialogComponent>,
        private lookupService: LookupService,
        private vendaDocumentoService: VendaDocumentoService,
        private compraService: CompraService,
    ) {}

    public ngOnInit(): void {
        this.carregarEstoque();
        this.carregarVendas();
        this.carregarCompras();
    }

    // ===================================================================== estoque

    private carregarEstoque(): void {
        this.lookupService.getProdutoSaldoLocal(this.data.idProduto).subscribe({
            next: (res) => {
                this.carregandoEstoque = false;
                // Sem saldo em localização nenhuma é resposta válida, não erro.
                this.locais = res.isError ? [] : (res.list ?? []);
            },
            error: () => {
                this.carregandoEstoque = false;
                this.locais = [];
            },
        });
    }

    public get totalFisico(): number {
        return this.somar((l) => l.saldoFisico);
    }

    public get totalReservado(): number {
        return this.somar((l) => l.saldoReservado);
    }

    public get totalDisponivel(): number {
        return this.somar((l) => l.saldoDisponivel);
    }

    /**
     * Mínimo e máximo são cadastrados POR LOCALIZAÇÃO, então a soma é o ponto de reposição do
     * produto inteiro. Localização sem reposição configurada entra como zero na soma, mas não
     * inventa número: se NENHUMA tiver, o getter devolve nulo e a linha mostra um traço.
     */
    public get estoqueMinimo(): number | null {
        return this.somarOpcional((l) => l.estoqueMinimo);
    }

    public get estoqueMaximo(): number | null {
        return this.somarOpcional((l) => l.estoqueMaximo);
    }

    /** O que sobra no depósito se este documento for faturado como está (§6.3). */
    public get estoqueDepoisDaVenda(): number {
        return this.totalDisponivel - (Number(this.data.quantidade) || 0);
    }

    /**
     * A quantidade da linha não cabe no disponível.
     *
     * É AVISO, e não bloqueio: quem decide se o documento pode ser salvo com estoque insuficiente
     * é a configuração do processo (`isBloquearEstoqueInsuficiente`), na hora de salvar.
     */
    public get faltaEstoque(): boolean {
        return !this.carregandoEstoque && this.estoqueDepoisDaVenda < 0;
    }

    private somar(campo: (l: ControleEstoqueLocalStruct) => number): number {
        return this.locais.reduce((soma, l) => soma + (Number(campo(l)) || 0), 0);
    }

    private somarOpcional(campo: (l: ControleEstoqueLocalStruct) => number | null | undefined): number | null {
        const informados = this.locais.filter((l) => campo(l) != null);

        return informados.length
            ? informados.reduce((soma, l) => soma + (Number(campo(l)) || 0), 0)
            : null;
    }

    // ===================================================================== últimas vendas

    private carregarVendas(): void {
        // Sem cliente escolhido não há o que perguntar: a aba tem estado vazio próprio, e uma
        // chamada com id nulo só gastaria uma ida ao servidor para voltar lista vazia.
        if (!this.data.idPessoaCliente) {
            this.carregandoVendas = false;
            return;
        }

        this.vendaDocumentoService
            .getHistoricoDoProdutoParaCliente(this.data.idPessoaCliente, this.data.idProduto)
            .subscribe({
                next: (res) => {
                    this.carregandoVendas = false;
                    this.vendas = res.isError ? [] : (res.vendas ?? []);
                },
                error: () => {
                    this.carregandoVendas = false;
                    this.vendas = [];
                },
            });
    }

    public get semCliente(): boolean {
        return !this.data.idPessoaCliente;
    }

    /**
     * A cor da etapa, do catálogo do kanban — nunca hexadecimal vinda do servidor.
     *
     * É o contrato do board: o catálogo traz um trio para o tema claro e outro para o escuro, e
     * quem sabe o tema é a tela. Catálogo ausente cai em cinza, e o histórico aparece igual.
     */
    public corDaEtapa(venda: VendaDoProdutoParaClienteStruct): PvCorVm {
        return resolverCor(venda.idKanbanCor ?? 0, this.data.coresKanban ?? [], this.isDarkMode);
    }

    private get isDarkMode(): boolean {
        return document.body.classList.contains('dark-mode');
    }

    // ===================================================================== compras previstas

    private carregarCompras(): void {
        this.compraService.getComprasPrevistasDoProduto(this.data.idProduto).subscribe({
            next: (res) => {
                this.carregandoCompras = false;
                this.compras = res.isError ? [] : (res.listaCompras ?? []);
            },
            error: () => {
                this.carregandoCompras = false;
                this.compras = [];
            },
        });
    }

    /**
     * A previsão de chegada, em texto.
     *
     * São dois dados diferentes e nenhum é obrigatório: a DATA que o solicitante pediu e o PRAZO
     * em dias que o fornecedor deu. A data ganha, porque é uma promessa mais específica; o prazo
     * entra quando ela não existe; sem os dois, o vendedor precisa ler "sem previsão" em vez de
     * uma célula vazia que ele confundiria com carregamento.
     */
    public previsaoDe(compra: ProdutoCompraPrevistaStruct): string {
        if (compra.dataEntregaDesejada) {
            // Data pura ("yyyy-MM-dd"): montada por componentes, porque new Date(iso) cairia no dia anterior no Brasil.
            const [ano, mes, dia] = compra.dataEntregaDesejada.substring(0, 10).split('-').map(Number);
            return new Date(ano, mes - 1, dia).toLocaleDateString('pt-BR');
        }

        const dias = Number(compra.prazoEntregaDias ?? 0);

        if (dias > 0) return `${dias} ${dias === 1 ? 'dia' : 'dias'}`;

        return 'Sem previsão';
    }

    // ===================================================================== casca

    /** A linha abaixo do título: o item a que o diálogo se refere, com SKU e NCM quando houver. */
    public get subtitulo(): string {
        const partes: string[] = [this.data.descricao || 'Item'];

        if (this.data.codigoSku) partes.push(`SKU ${this.data.codigoSku}`);
        if (this.data.codigoNcm) partes.push(`NCM ${this.data.codigoNcm}`);

        return partes.join(' · ');
    }

    public fechar(): void {
        this.dialogRef.close();
    }
}
